/**
 * WebMCP Implementation
 * Exposes site tools to AI agents via navigator.modelContext.provideContext()
 * See: https://webmachinelearning.github.io/webmcp/
 */

const DOMAIN = "https://bellaroseclinics.ae";
const WHATSAPP = "https://wa.me/971545846604";

interface WebMCPTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (args: Record<string, unknown>) => Promise<unknown>;
}

const tools: WebMCPTool[] = [
  {
    name: "book_appointment",
    description: "Book a medical appointment at Bella Rose Medical Center via WhatsApp. Open 7 days, 10AM-10PM.",
    inputSchema: {
      type: "object",
      properties: {
        service: { type: "string", description: "Treatment name (e.g., teeth cleaning, botox, hair transplant)" },
        department: { type: "string", enum: ["dental", "dermatology", "weightloss", "hair", "laser", "facial", "ivdrip", "endolift", "surgery", "hijama"] },
        language: { type: "string", enum: ["en", "ar"], default: "ar" },
      },
      required: ["service"],
    },
    execute: async (args) => {
      const service = args.service as string;
      const lang = (args.language as string) || "ar";
      const msg = lang === "en"
        ? `Hi, I'd like to book an appointment for ${service} at Bella Rose Medical Center.`
        : `مرحباً، أود حجز موعد لـ ${service} في عيادة بيلا روز.`;
      const url = `${WHATSAPP}?text=${encodeURIComponent(msg)}`;
      return { success: true, bookingUrl: url, message: `Booking link generated for ${service}`, phone: "+971 4 296 4442" };
    },
  },
  {
    name: "get_service_price",
    description: "Get the price of a specific medical service at Bella Rose Medical Center in AED",
    inputSchema: {
      type: "object",
      properties: {
        service: { type: "string", description: "Service name to look up price for" },
      },
      required: ["service"],
    },
    execute: async (args) => {
      const service = (args.service as string).toLowerCase();
      const prices: Record<string, { price: string; department: string }> = {
        "teeth cleaning": { price: "100 AED", department: "Dental" },
        "teeth whitening": { price: "700 AED", department: "Dental" },
        "regular filling": { price: "250 AED", department: "Dental" },
        "root canal": { price: "800 AED", department: "Dental" },
        "crown": { price: "800 AED", department: "Dental" },
        "metal braces": { price: "3,500 AED (1 jaw) / 6,000 AED (both)", department: "Dental" },
        "clear aligners": { price: "7,000 AED", department: "Dental" },
        "implant german": { price: "4,500 AED", department: "Dental" },
        "implant french": { price: "3,500 AED", department: "Dental" },
        "hollywood smile 3d": { price: "700 AED/tooth", department: "Dental" },
        "hollywood smile 6d": { price: "1,200 AED/tooth", department: "Dental" },
        "gum botox": { price: "650 AED", department: "Dental" },
        "botox korean": { price: "650 AED", department: "Dermatology" },
        "botox allergan": { price: "1,200 AED", department: "Dermatology" },
        "filler korean": { price: "1,200 AED", department: "Dermatology" },
        "lip filler": { price: "2,000 AED", department: "Dermatology" },
        "under-eye filler": { price: "1,500 AED", department: "Dermatology" },
        "nose filler": { price: "1,500 AED", department: "Dermatology" },
        "exosome": { price: "2,500 AED", department: "Dermatology" },
        "skin booster": { price: "1,200 AED", department: "Dermatology" },
        "sculptra": { price: "3,500 AED", department: "Dermatology" },
        "appetite suppressant": { price: "550 AED", department: "Weight Loss" },
        "fat dissolving": { price: "800 AED", department: "Weight Loss" },
        "double chin dissolving": { price: "1,000 AED", department: "Weight Loss" },
        "onda": { price: "1,500 AED", department: "Weight Loss" },
        "coolsculpting": { price: "2,000 AED", department: "Weight Loss" },
        "prp hair": { price: "550 AED", department: "Hair" },
        "mesotherapy hair": { price: "550 AED", department: "Hair" },
        "exosome hair": { price: "2,500 AED", department: "Hair" },
        "gfc hair": { price: "1,500 AED", department: "Hair" },
        "hair transplant": { price: "from 7,000 AED", department: "Hair" },
        "laser hair removal": { price: "from 150 AED", department: "Laser" },
        "carbon laser": { price: "350 AED", department: "Laser" },
        "fractional co2": { price: "1,200 AED", department: "Laser" },
        "tattoo removal": { price: "500 AED", department: "Laser" },
      };

      // Fuzzy match
      const match = Object.entries(prices).find(([key]) => 
        service.includes(key) || key.includes(service)
      );

      if (match) {
        return { found: true, service: match[0], price: match[1].price, department: match[1].department, bookingUrl: WHATSAPP };
      }
      return { found: false, message: `Price not found for "${args.service}". Contact us for pricing.`, allPricesUrl: `${DOMAIN}/services`, bookingUrl: WHATSAPP };
    },
  },
  {
    name: "get_clinic_info",
    description: "Get Bella Rose Medical Center location, hours, contact details, and directions",
    inputSchema: {
      type: "object",
      properties: {},
    },
    execute: async () => {
      return {
        name: "Bella Rose Medical Center",
        address: "Al Mamzar, Dubai, UAE",
        phone: "+971 4 296 4442",
        whatsapp: "+971 54 584 6604",
        website: DOMAIN,
        email: "info@bellaroseclinics.ae",
        hours: "Open 7 days a week, 10:00 AM – 10:00 PM",
        googleMaps: "https://maps.app.goo.gl/WLR3tdjmDChx5coh8",
        coordinates: { lat: 25.2949675, lng: 55.3511937 },
        departments: 10,
        doctors: 9,
        services: "70+",
        paymentMethods: ["Cash", "Visa", "Mastercard", "Apple Pay", "Samsung Pay", "Tabby (4 installments)", "Tamara (4 installments)"],
        social: {
          instagram: "https://www.instagram.com/bellarosemc.ae/",
          facebook: "https://www.facebook.com/bellarosemc.ae",
          tiktok: "https://www.tiktok.com/@bellarose_medical_center",
        },
      };
    },
  },
  {
    name: "find_doctor",
    description: "Find a doctor at Bella Rose Medical Center by department",
    inputSchema: {
      type: "object",
      properties: {
        department: { type: "string", description: "Department name (dental, dermatology, weightloss, hair, laser, facial, ivdrip, endolift, surgery, hijama)" },
      },
    },
    execute: async (args) => {
      const dept = ((args.department as string) || "").toLowerCase();
      const doctors: Record<string, string[]> = {
        dental: ["Dr. Ahmed", "Dr. Mohamed", "Dr. Abdullah", "Dr. Ramy", "Dr. Ammar"],
        dermatology: ["Dr. Radwa", "Dr. Fatima Salah", "Dr. Mohamed Khadour"],
        weightloss: ["Dr. Radwa", "Dr. Fatima Salah", "Dr. Farouk", "Dr. Ali Ahmad"],
        hair: ["Dr. Radwa", "Dr. Fatima Salah", "Dr. Samira"],
        laser: ["Dr. Radwa", "Dr. Fatima Salah"],
        facial: ["Dr. Radwa", "Dr. Fatima Salah", "Dr. Mohamed Khadour"],
        ivdrip: ["Dr. Radwa", "Dr. Fatima Salah"],
        endolift: ["Dr. Radwa", "Dr. Fatima Salah", "Dr. Farouk", "Dr. Ali Ahmad"],
        surgery: ["Dr. Farouk"],
        hijama: ["Dr. Radwa"],
      };

      const found = doctors[dept];
      if (found) {
        return { department: dept, doctors: found.map(name => ({ name, credentials: "DHA Licensed" })), bookingUrl: WHATSAPP };
      }
      return { 
        message: "All departments available", 
        departments: Object.keys(doctors),
        totalDoctors: 12,
        allDHALicensed: true,
        bookingUrl: WHATSAPP,
      };
    },
  },
  {
    name: "check_current_offers",
    description: "Check current promotions and deals at Bella Rose Medical Center",
    inputSchema: {
      type: "object",
      properties: {},
    },
    execute: async () => {
      return {
        offers: [
          { title: "Free Consultation + 10% Off First Treatment", applicable: "All departments" },
          { title: "Hollywood Smile Package Deals", applicable: "Dental" },
          { title: "Weight Loss Starter Packages", applicable: "Weight Loss" },
          { title: "Dental Implant Bundle Offers", applicable: "Dental" },
        ],
        paymentPlans: {
          tabby: "Split into 4 interest-free payments",
          tamara: "Split into 4 interest-free payments",
        },
        offersPage: `${DOMAIN}/offers`,
        bookingUrl: WHATSAPP,
      };
    },
  },
];

/**
 * Initialize WebMCP - call this on page load
 */
export function initWebMCP() {
  // Check if WebMCP API is available
  if (typeof navigator === "undefined") return;
  
  const nav = navigator as unknown as { modelContext?: { provideContext: (ctx: unknown) => void } };
  
  if (!nav.modelContext?.provideContext) {
    // WebMCP not supported in this browser, silently skip
    return;
  }

  try {
    nav.modelContext.provideContext({
      name: "Bella Rose Medical Center",
      description: "Dubai's premier multi-specialty medical center. Book appointments, get prices, find doctors, and access medical information for 70+ treatments across 10 departments.",
      tools: tools.map(tool => ({
        name: tool.name,
        description: tool.description,
        inputSchema: tool.inputSchema,
        execute: tool.execute,
      })),
    });
  } catch (e) {
    // Silently fail if WebMCP initialization fails
    console.debug("[WebMCP] Not available:", e);
  }
}
